import { EmptyState } from '@airship-platform/jam-web/components';
import { Product, ProductsQuery } from '@airship-platform/airship-proposal';
import Button from '@mui/material/Button';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { useAppointmentProposalUpgrades } from '../../../../data-access/appointmentProposalUpgrades';
import { useAppointmentProposals } from '../../../../data-access/appointmentProposals';
import { SectionWrapper } from '../../../../patterns';

const CreateUpgradePage: React.FC = () => {
  const { currentProposal } = useAppointmentProposals();

  const {
    isLoading: isProposalUpgradesLoading,
    proposalUpgrades,
    createAppointmentProposalUpgrade,
    deleteAppointmentProposalUpgrade,
  } = useAppointmentProposalUpgrades();

  const proposalUpgradesMap = useMemo(
    () => new Map(proposalUpgrades.map((u) => [u.pricebookId, u])),
    [proposalUpgrades]
  );

  if (!currentProposal) {
    return (
      <EmptyState
        title="No proposal found"
        subtitle="Please create a proposal first"
      />
    );
  }

  const onUpsertProposalUpgrade = async (product: Product) => {
    const proposalUpgrade = proposalUpgradesMap.get(product.pricebookId);
    if (proposalUpgrade) {
      await deleteAppointmentProposalUpgrade(
        proposalUpgrade.appointmentId,
        proposalUpgrade.appointmentProposalId,
        proposalUpgrade.appointmentProposalUpgradeId
      );
    } else {
      await createAppointmentProposalUpgrade(
        currentProposal.appointmentId,
        currentProposal.appointmentProposalId,
        {
          pricebookId: product.pricebookId,
          jobId: currentProposal.jobId,
          type: product.type,
          sortOrder:
            Math.max(0, ...proposalUpgrades.map((u) => u.sortOrder)) + 1,
        }
      );
    }
  };

  return (
    <SectionWrapper
      maxWidth={'md'}
      width={'100%'}
      header="Create upgrade"
      subheader="Add an upgrade to this single proposal."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../upgrades',
          label: 'Cancel',
        },
      }}
    >
      <ProductsQuery
        renderProductAction={(product) => (
          <Button
            color={
              proposalUpgradesMap.has(product.pricebookId)
                ? 'primary'
                : 'softGold'
            }
            size="small"
            disabled={isProposalUpgradesLoading}
            onClick={() => onUpsertProposalUpgrade(product)}
            sx={{ minWidth: 120, alignSelf: 'center' }}
          >
            {proposalUpgradesMap.has(product.pricebookId)
              ? 'Remove upgrade'
              : 'Add upgrade'}
          </Button>
        )}
      />
    </SectionWrapper>
  );
};

export default CreateUpgradePage;
